import { Component, Input, OnInit } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';
import { MenuController, ModalController, ToastController } from '@ionic/angular';
import { cloneDeep } from 'lodash-es';
import { ProductType } from 'src/app/shared-lib/domains/product';
import { CartService } from 'src/app/shared/cart.service';
import { uidI } from 'src/app/shared/utils/utils';
import { ChangeTextFieldModalComponent } from '../change-text-field-modal/change-text-field-modal.component';
import { DeleteFolderConfirmationComponent } from '../delete-folder-confirmation/delete-folder-confirmation.component';
import { PrintProductModalComponent } from '../print-product-modal/print-product-modal.component';

@Component({
  selector: 'app-folder-actions-menu',
  templateUrl: './folder-actions-menu.component.html',
  styleUrls: ['./folder-actions-menu.component.scss'],
  standalone: false
})
export class FolderActionsMenuComponent implements OnInit {

  @Input() inCart: boolean = false;
  @Input() modal: boolean = false;
  @Input() item;
  @Input() inOrderDetail: boolean = false;
  @Input() currentPrintProduct = null;

  constructor(
    private router: Router,
    private menuCtrl: MenuController,
    public cartService: CartService,
    private modalController: ModalController,
    private toastController: ToastController
  ) { }

  ngOnInit() { }

  editFolder() {
    if (this.router.url === '/') {
      this.cartService.setActivePrintItem(this.item);
    } else {
      const navigationExtras: NavigationExtras = { state: { item: this.item } };
      this.router.navigate(['/'], navigationExtras);
    }
    this.menuCtrl.close('cart-preview');
    this.modalController.dismiss();
  }

  async changeFolderName() {
    const modal = await this.modalController.create({
      component: ChangeTextFieldModalComponent,
      backdropDismiss: false,
      componentProps: {
        currentText: this.item.product.folderName,
        label: 'Escribe el nuevo nombre para la carpeta',
        placeholder: 'Escribe aquí el nombre de la carpeta',
        title: 'Renombrar carpeta',
        titleIcon: 'assets/icon/rename-blue-circle.svg',
        maxLength: 25,
      },
      cssClass: 'auto-height medium-width',
    });
    await modal.present();

    modal.onDidDismiss().then((resp) => {
      const { data } = resp;
      if (data) {
        this.item.product.folderName = data.newText;
        this.cartService.saveCart(this.cartService.cart);
        if (this.toastController.getTop()) {
          this.toastController.dismiss();
        }
        this.toastController
          .create({
            message: 'Carpeta renombrada con éxito',
            color: 'success',
            position: 'bottom',
            cssClass: 'fitWidthToast',
            duration: 3000,
            icon: 'assets/icon/circle-check.svg',
          })
          .then((toast) => toast.present());
      }
    });
  }

  async viewDocs() {
    const modal = await this.modalController.create({
      component: PrintProductModalComponent,
      componentProps: {
        item: this.item,
        inOrderDetail: this.inOrderDetail,
      },
      cssClass: 'print-product-modal',
    });
    modal.present();
  }

  async changeComment() {
    const confirmed = await this.cartService.showConfirmCommentInfo(null);
    if (!confirmed) return;

    const modal = await this.modalController.create({
      component: ChangeTextFieldModalComponent,
      backdropDismiss: false,
      componentProps: {
        currentText: this.item.product.printingGroup.comment,
        label: 'Escribe un comentario',
        placeholder: 'Escribe aquí tu comentario',
        allowEmpty: true,
        title: `Comentario para ${this.item.product.folderName}`,
        titleIcon: 'assets/icon/comment-blue-circle.svg',
        maxLength: 100,
      },
      cssClass: 'auto-height medium-width',
    });
    await modal.present();

    modal.onDidDismiss().then(async (resp) => {
      const { data } = resp;
      if (data) {
        this.item.product.printingGroup.comment = data.newText;
        this.cartService.saveCart(this.cartService.cart);
        if (this.toastController.getTop()) {
          this.toastController.dismiss();
        }
        this.toastController
          .create({
            message: `El comentario para ${this.item.product.folderName} ha sido añadido con éxito.`,
            color: 'success',
            position: 'bottom',
            cssClass: 'fitWidthToast',
            duration: 3000,
            icon: 'assets/icon/circle-check.svg',
          })
          .then((toast) => toast.present());
      }
    });
  }

  async duplicateFolder() {
    const folders = this.cartService.cart.items.filter(
      (item) => item.product.type === ProductType.PRINT && !item.product.vendor
    );
    const duplicatedFolder = cloneDeep(this.item);
    duplicatedFolder.product.folderName = `Carpeta ${folders.length + 1}`;
    duplicatedFolder.cartId = uidI();
    this.cartService.cart.items.push(duplicatedFolder);
    this.cartService.setActivePrintItem(duplicatedFolder);
    this.cartService.refreshCartPrice();
    const toast = await this.toastController.create({
      icon: 'assets/icon/circle-check.svg',
      message: 'Tu carpeta ha sido duplicada con éxito',
      color: 'success',
      position: 'bottom',
      animated: true,
      cssClass: 'fitWidthToast successToast',
      duration: 3000,
    });
    await toast.present();
  }

  async removePrintGroup(): Promise<void> {
    return new Promise(async (resolve) => {
      const modal = await this.modalController.create({
        component: DeleteFolderConfirmationComponent,
        backdropDismiss: false,
        componentProps: {
          group: this.item,
          currentPrintProduct: this.currentPrintProduct,
        },
        cssClass: 'auto-height medium-width',
      });
      await modal.present();

      modal.onDidDismiss().then(async (resp) => {
        const { data } = resp;
        if (data) {
          const it = this.cartService.cart.items.findIndex((_item) => this.item.cartId === _item.cartId);
          this.cartService.cart.items.splice(it, 1);
          this.cartService.refreshCartPrice();
          this.cartService.setActivePrintItem(
            this.cartService.getPrintProductsInCart().reverse()[0]
          );
          this.cartService.deleteOutOfStockPopover(this.item.cartId);

          if (this.toastController.getTop()) {
            this.toastController.dismiss();
          }

          const toast = await this.toastController.create({
            message: `${this.item.product.folderName} ha sido eliminada con éxito`,
            color: 'success',
            position: 'bottom',
            cssClass: 'fitWidthToast',
            duration: 3000,
            icon: 'assets/icon/circle-check.svg',
          });
          await toast.present();

          if (this.modal) {
            this.modalController.dismiss();
          }
        }
        resolve();
      });
    });
  }

}

